import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Award, ArrowRight, ChevronRight, Home, Building2, ShieldCheck, Sparkles, Cog, Factory } from 'lucide-react';
import SEO, { buildBreadcrumbSchema } from '../components/SEO';
import { SITE_URL, BRAND, CRILAM_FACTS } from '../lib/seoContent';

/**
 * FounderPage — E-E-A-T authority page for the person behind CriLam's CriLam PUR-Bond™ technology.
 * Uses PLACEHOLDER content per handoff — user will supply the real founder bio & photo later.
 */
const FounderPage = () => {
  const url = `${SITE_URL}/founder`;
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: 'The CriLam PUR Master',
      jobTitle: 'Founder & Master of PUR Lamination',
      worksFor: {
        '@type': 'Organization',
        name: BRAND,
        url: `${SITE_URL}/`,
      },
      description:
        "Founder of CriLam Decorative Pvt. Ltd. — pioneered one of the largest PUR-bonded lamination facilities in North India. Two decades of hands-on decorative-laminate expertise, specializing in Polyurethane Reactive bonding for Pre-Laminated 6H Acrylic Boards.",
      knowsAbout: [
        'PUR (Polyurethane Reactive) Lamination',
        '6H Acrylic Laminate Manufacturing',
        'Pre-Laminated Board Engineering',
        'Nail-Free Door Construction',
        'CNC Door Design',
        'Mirror Finish Acrylic Boards',
      ],
      url,
    },
    buildBreadcrumbSchema([
      { name: 'Home', url: `${SITE_URL}/` },
      { name: 'About', url: `${SITE_URL}/about` },
      { name: 'Founder', url },
    ]),
  ];

  return (
    <div className="min-h-screen bg-[#F7F8F5]" data-testid="founder-page">
      <SEO
        title={`The CriLam PUR Master — Founder & Lamination Expert | ${BRAND}`}
        description="Meet the founder of CriLam Decorative Pvt. Ltd. — the visionary behind India's premier PUR-bonded Pre-Laminated 6H Acrylic Boards. Two decades of decorative-laminate expertise in Kanpur, UP."
        canonical={url}
        keywords="CriLam founder, PUR master, acrylic laminate expert India, decorative laminate expert Kanpur, lamination engineer UP"
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative bg-[#0A0D0C] pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#004534]/40 to-transparent" />
        <div className="container mx-auto px-6 lg:px-12 relative z-10 max-w-5xl">
          <nav className="mb-6 text-xs text-white/50 flex items-center gap-2" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#D4AF37] flex items-center gap-1"><Home className="h-3 w-3" /> Home</Link>
            <ChevronRight className="h-3 w-3" />
            <Link to="/about" className="hover:text-[#D4AF37]">About</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-white/80">Founder</span>
          </nav>

          <div className="grid md:grid-cols-3 gap-10 items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="md:col-span-1"
            >
              {/* Placeholder portrait — user will replace */}
              <div className="aspect-[3/4] rounded-2xl overflow-hidden bg-gradient-to-br from-[#004534] to-[#0A0D0C] border-4 border-[#D4AF37]/40 shadow-2xl relative flex items-center justify-center">
                <div className="absolute inset-0 noise-overlay opacity-40" />
                <div className="text-center relative z-10 px-4">
                  <div className="w-24 h-24 mx-auto rounded-full bg-[#D4AF37] flex items-center justify-center text-4xl font-bold text-[#0A0D0C] mb-4">
                    C
                  </div>
                  <p className="text-white/70 text-sm">Founder Photo</p>
                  <p className="text-white/40 text-xs mt-1">Coming soon</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="md:col-span-2"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-[#0A0D0C] text-xs font-bold uppercase tracking-wider mb-6">
                <Award className="h-3.5 w-3.5" /> The PUR Master
              </div>
              <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 font-['Outfit'] leading-tight">
                Meet the Founder Behind CriLam PUR-Bond™ Technology
              </h1>
              <p className="text-lg text-[#D4AF37] font-medium mb-4">
                Two decades of decorative-laminate mastery — engineered from Kanpur, delivered pan-India.
              </p>
              <p className="text-white/70 leading-relaxed">
                CriLam Decorative Pvt. Ltd. was founded to answer a simple question: why do Indian homes have to accept laminate that fails in 3 years?
                The founder&apos;s answer was Polyurethane Reactive (PUR) technology — a factory-controlled molecular bond that outlasts water-based glue by 10×. This is the story of building it.
              </p>
              <p className="text-xs text-white/40 mt-4 italic">
                Full founder bio and portrait will be published in the next edition — this page is a placeholder while final details are being prepared.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Expertise pillars */}
      <section className="py-14">
        <div className="container mx-auto px-6 lg:px-12 max-w-5xl">
          <h2 className="text-2xl md:text-3xl font-bold text-[#004534] mb-8 font-['Outfit']">Areas of Expertise</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { icon: Cog, title: 'PUR Lamination', desc: 'Engineered one of North India\'s largest Polyurethane Reactive bonding lines — precision humidity, temperature, and pressure control.' },
              { icon: Sparkles, title: '6H Acrylic Boards', desc: 'Championed the shift from 4H to 6H hardness as an Indian premium standard — 3× scratch resistance for real-world use.' },
              { icon: ShieldCheck, title: 'Nail-Free Construction', desc: 'Pioneered nail-free CNC door manufacturing across four material families — Acrylic, Mica, Veneer, PU Paint.' },
              { icon: Factory, title: 'Kanpur Manufacturing', desc: 'Built CriLam\'s Kanpur facility as a benchmark for made-in-UP premium laminate — factory direct to pan-India dealers.' },
              { icon: Building2, title: 'Interior Ecosystem', desc: 'Product portfolio spans kitchens, wardrobes, main doors, wall panels, and pooja rooms — one CriLam PUR-Bond™ technology, every use case.' },
              { icon: Award, title: '10-Year Guarantee', desc: 'Calibrated field-tested 10-year written warranty — backed by hundreds of live installations of decade-old PUR-bonded boards.' },
            ].map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="bg-white rounded-xl border border-gray-200 p-5 hover:border-[#D4AF37]/50 transition-colors"
                >
                  <Icon className="h-6 w-6 text-[#D4AF37] mb-3" />
                  <h3 className="font-bold text-[#004534] mb-1.5 font-['Outfit']">{p.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{p.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Company snapshot */}
      <section className="py-14 bg-white border-y">
        <div className="container mx-auto px-6 lg:px-12 max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-bold text-[#004534] mb-6 font-['Outfit']">Company Snapshot</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-3 text-sm">
              <div><span className="font-semibold text-[#004534]">Company:</span> <span className="text-gray-700">{BRAND}</span></div>
              <div><span className="font-semibold text-[#004534]">Founded:</span> <span className="text-gray-700">{CRILAM_FACTS.founded}</span></div>
              <div><span className="font-semibold text-[#004534]">Headquarters:</span> <span className="text-gray-700">{CRILAM_FACTS.headquarters}</span></div>
              <div><span className="font-semibold text-[#004534]">Factory:</span> <span className="text-gray-700">{CRILAM_FACTS.factoryAddress}</span></div>
              <div><span className="font-semibold text-[#004534]">Technology:</span> <span className="text-gray-700">{CRILAM_FACTS.technology}</span></div>
            </div>
            <div className="space-y-3 text-sm">
              <div><span className="font-semibold text-[#004534]">Signature:</span> <span className="text-gray-700">{CRILAM_FACTS.signature}</span></div>
              <div><span className="font-semibold text-[#004534]">Warranty:</span> <span className="text-gray-700">{CRILAM_FACTS.warranty}</span></div>
              <div><span className="font-semibold text-[#004534]">Service Regions:</span> <span className="text-gray-700">{CRILAM_FACTS.serviceRegions}</span></div>
              <div><span className="font-semibold text-[#004534]">Phone:</span> <span className="text-gray-700">{CRILAM_FACTS.phone}</span></div>
              <div><span className="font-semibold text-[#004534]">Website:</span> <span className="text-gray-700">{CRILAM_FACTS.website}</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-gradient-to-br from-[#004534] to-[#0A0D0C] text-white">
        <div className="container mx-auto px-6 lg:px-12 max-w-3xl text-center">
          <h3 className="text-2xl md:text-3xl font-bold mb-4 font-['Outfit']">Walk the Factory. See the Craft.</h3>
          <p className="text-white/70 mb-8">Serious buyers and interior designers are welcome to tour the CriLam Kanpur facility.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/contact" data-testid="founder-cta-primary">
              <button className="px-8 py-4 bg-[#D4AF37] text-[#004534] font-bold rounded-lg hover:bg-white transition-colors flex items-center gap-2">
                Schedule a Factory Tour <ArrowRight className="h-5 w-5" />
              </button>
            </Link>
            <Link to="/crilam-facts">
              <button className="px-8 py-4 border-2 border-white/40 text-white font-medium rounded-lg hover:bg-white/10">
                CriLam Facts →
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FounderPage;
